import { useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useVeloraStore } from '../store/useVeloraStore';
import { useAudio } from '../hooks/useAudio';
import PitchVisualizer from '../components/PitchVisualizer';
import StabilityMeter from '../components/StabilityMeter';
import FeedbackOverlay from '../components/FeedbackOverlay';
import { centsToColor, pitchAccuracyLabel } from '../lib/noteMap';

export default function Session() {
  const theme = useTheme();
  const { start, stop } = useAudio();
  const {
    isRecording, currentNote, currentFrequency, currentCents,
    stability, score, isConnected, wsEnabled, transposeSemitones, setTranspose,
  } = useVeloraStore();

  const handleToggle = useCallback(async () => {
    if (isRecording) stop();
    else await start();
  }, [isRecording, start, stop]);

  const accuracyColor = centsToColor(currentCents);
  const accuracyLabel = currentNote !== '--' ? pitchAccuracyLabel(currentCents) : '';

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>
      <FeedbackOverlay />

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-4 flex items-center justify-between fade-up">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
            VELORA
          </h1>
          <p className="text-xs tracking-widest" style={{ color: theme.muted }}>Vocal Session</p>
        </div>
        <div className="flex items-center gap-2">
          {wsEnabled && (
            <div className="glass-pill flex items-center gap-1.5 px-2.5 py-1 text-xs"
              style={{ borderColor: isConnected ? 'rgba(74,222,128,0.35)' : 'rgba(248,113,113,0.25)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: isConnected ? '#4ade80' : '#f87171' }} />
              <span style={{ color: isConnected ? '#4ade80' : '#f87171', fontFamily: 'Syne, sans-serif' }}>
                {isConnected ? 'AI ON' : 'AI OFF'}
              </span>
            </div>
          )}
          {isRecording && (
            <div className="glass-pill flex items-center gap-1.5 px-2.5 py-1 text-xs"
              style={{ borderColor: 'rgba(248,113,113,0.35)' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#f87171' }} />
              <span style={{ color: '#f87171', fontFamily: 'Syne, sans-serif' }}>REC</span>
            </div>
          )}
        </div>
      </div>

      {/* Note hero */}
      <div className="flex flex-col items-center py-5 px-4 fade-up-1">
        <div className="relative flex items-center justify-center mb-3">
          {isRecording && currentNote !== '--' && (
            <>
              <div className="absolute w-28 h-28 rounded-full animate-pulse-ring"
                style={{ background: `${accuracyColor}15`, border: `1px solid ${accuracyColor}35` }} />
              <div className="absolute w-28 h-28 rounded-full animate-pulse-ring-2"
                style={{ background: `${accuracyColor}08`, border: `1px solid ${accuracyColor}20` }} />
            </>
          )}
          <div className="relative w-28 h-28 rounded-full flex items-center justify-center glass-card"
            style={{
              background: currentNote !== '--'
                ? `radial-gradient(circle, ${accuracyColor}15, rgba(255,255,255,0.04))`
                : undefined,
              borderColor: currentNote !== '--' ? `${accuracyColor}40` : undefined,
              boxShadow: currentNote !== '--'
                ? `0 0 40px ${accuracyColor}25, inset 0 1px 0 rgba(255,255,255,0.15)`
                : undefined,
              transition: 'all 0.12s ease',
            }}>
            <span className="text-4xl font-bold text-glow"
              style={{
                color: currentNote !== '--' ? accuracyColor : theme.dim,
                fontFamily: 'Syne, sans-serif',
                textShadow: currentNote !== '--' ? `0 0 20px ${accuracyColor}70` : 'none',
              }}>
              {currentNote}
            </span>
          </div>
        </div>

        {/* Frequency + accuracy */}
        <div className="flex items-center gap-3 text-sm mb-3">
          {currentFrequency > 0 && (
            <span className="font-mono text-xs" style={{ color: theme.muted }}>
              {currentFrequency.toFixed(1)} Hz
            </span>
          )}
          {accuracyLabel && (
            <span className="font-semibold text-xs" style={{ color: accuracyColor }}>{accuracyLabel}</span>
          )}
          {currentNote !== '--' && (
            <span className="font-mono text-xs"
              style={{ color: Math.abs(currentCents) <= 5 ? '#4ade80' : theme.muted }}>
              {currentCents > 0 ? '+' : ''}{currentCents}¢
            </span>
          )}
        </div>

        {/* Cents deviation bar */}
        <div className="relative w-52 h-1.5 rounded-full overflow-hidden glass-card" style={{ borderRadius: 50 }}>
          <div className="absolute inset-y-0 left-1/2 w-px" style={{ background: 'rgba(255,255,255,0.15)' }} />
          <div className="absolute inset-y-0 rounded-full transition-all duration-100"
            style={{
              width: 5,
              left: `calc(50% + ${Math.max(-47, Math.min(47, currentCents * 0.47))}px - 2.5px)`,
              background: accuracyColor,
              boxShadow: `0 0 8px ${accuracyColor}`,
            }} />
        </div>
      </div>

      {/* Piano roll */}
      <div className="px-4 mb-3 fade-up-2">
        <PitchVisualizer />
      </div>

      {/* Stability */}
      <div className="px-4 mb-3 fade-up-3">
        <StabilityMeter stability={stability} />
      </div>

      {/* Score + transpose */}
      <div className="px-4 mb-3 flex flex-col gap-3 fade-up-4">
        <div className="glass-card rounded-2xl p-3.5 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-xs tracking-widest uppercase mb-1"
              style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Session Score</p>
            <div className="flex items-end gap-1">
              <span className="text-3xl font-bold" style={{ color: theme.accent, fontFamily: 'Syne, sans-serif' }}>
                {score}
              </span>
              <span className="text-sm mb-1" style={{ color: theme.dim }}>/ 100</span>
            </div>
          </div>
          <svg width="52" height="52" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.8"/>
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={theme.accent} strokeWidth="2.8"
              strokeDasharray={`${score} 100`} strokeDashoffset="25"
              style={{ transition: 'stroke-dasharray 0.5s ease', filter: `drop-shadow(0 0 3px ${theme.accent}70)` }}
              strokeLinecap="round"/>
            <text x="18" y="22.5" textAnchor="middle" fontSize="9" fill={theme.accent} fontFamily="JetBrains Mono, monospace">
              {score}%
            </text>
          </svg>
        </div>

        <div className="glass-card rounded-2xl px-4 py-3">
          <div className="flex justify-between text-xs mb-2" style={{ color: theme.muted }}>
            <span className="tracking-widest uppercase" style={{ fontFamily: 'Syne, sans-serif' }}>Transpose</span>
            <span className="font-mono font-semibold"
              style={{ color: transposeSemitones !== 0 ? theme.accent : theme.dim }}>
              {transposeSemitones > 0 ? '+' : ''}{transposeSemitones} st
            </span>
          </div>
          <input type="range" min={-12} max={12} value={transposeSemitones}
            onChange={(e) => setTranspose(Number(e.target.value))}
            className="w-full cursor-pointer"
            style={{ accentColor: theme.accent }} />
        </div>
      </div>

      {/* Record button */}
      <div className="fixed bottom-0 left-0 right-0 flex justify-center pointer-events-none"
        style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', paddingTop: 16,
          background: `linear-gradient(to top, ${theme.bg}e0 40%, transparent)` }}>
        <button onClick={handleToggle}
          className="pointer-events-auto w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90"
          style={{
            background: isRecording
              ? 'rgba(248,113,113,0.18)'
              : 'rgba(255,255,255,0.09)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: `2px solid ${isRecording ? 'rgba(248,113,113,0.55)' : 'rgba(255,255,255,0.18)'}`,
            boxShadow: `0 0 ${isRecording ? 32 : 20}px ${isRecording ? 'rgba(248,113,113,0.25)' : 'rgba(255,255,255,0.12)'}, inset 0 1px 0 rgba(255,255,255,0.15)`,
          }}>
          {isRecording ? (
            <span className="w-6 h-6 rounded-md" style={{ background: '#f87171' }} />
          ) : (
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="6" fill={theme.text}/>
              <circle cx="12" cy="12" r="10" stroke={theme.accent} strokeWidth="1.5" fill="none"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
